import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { extractOrgIdFromPath } from '../../../shared/utils/organizationUtils';
import { useMobile } from '../../../shared/hooks/useMobile';

import DualCalendarDropdown from '../../../shared/components/ui/DualCalendarDropdown';
import CreateMatchModal from './CreateMatchModal';
import DeleteAlert from '../../../shared/components/ui/DeleteAlert';
import Toast from '../../../shared/components/ui/Toast';
import ScoreAppHeader from '../../../shared/components/layout/ScoreAppHeader';
import { accessControlService } from '../../../shared/services/accessControl';
import Button from '../../../shared/components/ui/Button';
import { useMatches } from '../../../shared/hooks/useMatches';
import { useFavorites } from '../../../shared/contexts/FavoritesContext';
import { supabase } from '../../../shared/lib/supabase';
import { transformMatchToUI } from '../../../shared/utils/dataTransformers';
import VideoLinkModal from '../../../shared/components/ui/VideoLinkModal';
import MatchPointsModal from './MatchPointsModal';
import { useAllMatchPoints } from '../../../shared/hooks/useMatchPoints';
import { useStatusFilter } from '../../../shared/hooks/useStatusFilter';
import StatusFilterDropdown, { type BasicStatus } from '../../../shared/components/ui/StatusFilterDropdown';
import ViewModeToggle, { type ViewMode } from '../../../shared/components/ui/ViewModeToggle';
import SortModeToggle, { type SortMode } from '../../../shared/components/ui/SortModeToggle';
import MatchCard from '../../../shared/components/ui/MatchCard';

// Reusable loading component for consistency
const TabLoader: React.FC<{ message: string }> = ({ message }) => (
  <div className="flex items-center justify-center py-8">
    <div className="flex items-center space-x-2">
      <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      <span className="text-sm text-gray-600">{message}</span>
    </div>
  </div>
);

interface Match {
  id: string;
  name: string;
  date: string;
  time: string;
  tournament: {
    id: string;
    name: string;
    logo: string;
  };
  homeTeam: {
    name: string;
    logo: string;
    score: number;
  };
  awayTeam: {
    name: string;
    logo: string;
    score: number;
  };
  homePenaltyScore?: number;
  awayPenaltyScore?: number;
  isFavorite: boolean;
  venue: string;
  status: 'upcoming' | 'live' | 'finished';
}

interface MatchSelectionScreenProps {
  isMobile?: boolean;
  onToggleSidebar?: () => void;
}

const MatchSelectionScreen: React.FC<MatchSelectionScreenProps> = ({ isMobile = false, onToggleSidebar }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile: detectedMobile } = useMobile();
  const actualIsMobile = isMobile || detectedMobile;
  const [activeTopTab, setActiveTopTab] = useState<'all' | 'favourites'>('all');
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [sortMode, setSortMode] = useState<SortMode>('relevant');
  const [statusSelection, setStatusSelection] = useState<BasicStatus[]>(['upcoming', 'live', 'completed']);
  // status handled by useStatusFilter with URL sync
  const [favoritesVersion, setFavoritesVersion] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');

  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedStartDate, setSelectedStartDate] = useState<Date | null>(null);
  const [selectedEndDate, setSelectedEndDate] = useState<Date | null>(null);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteAlertOpen, setIsDeleteAlertOpen] = useState(false);
  const [isVideoLinkModalOpen, setIsVideoLinkModalOpen] = useState(false);
  const [isMatchPointsModalOpen, setIsMatchPointsModalOpen] = useState(false);
  const [selectedMatchForEdit, setSelectedMatchForEdit] = useState<any>(null);
  const [selectedMatchForDelete, setSelectedMatchForDelete] = useState<any>(null);
  const [selectedMatchForVideoLink, setSelectedMatchForVideoLink] = useState<any>(null);
  const [selectedMatchForPoints, setSelectedMatchForPoints] = useState<any>(null);
  const [toast, setToast] = useState<{isVisible: boolean, type: 'success' | 'error' | 'info', message: string}>({
    isVisible: false,
    type: 'success',
    message: ''
  });
  const dateButtonRef = useRef<HTMLButtonElement>(null);

  // Get match points to check which matches have saved points
  const { matchPoints } = useAllMatchPoints();

  // Helper function to check if a match has saved points
  const hasMatchPoints = (matchId: string): boolean => {
    return matchPoints.some(point => point.match_id === matchId);
  };

  // Use real data from database
  const { matches: dbMatches, loading, error, loadMatches } = useMatches();
  const { favoriteTournaments } = useFavorites();
  
  // Force re-render when favorites change
  useEffect(() => {
    setFavoritesVersion(prev => prev + 1);
  }, [favoriteTournaments]);

  // Transform database matches to UI format
  const matches: Match[] = dbMatches ? dbMatches.map(match => ({
    ...transformMatchToUI(match),
    isFavorite: false // Removed match favorites functionality
  })) : [];

  // Time helpers to match other apps' sorting behavior (distance from now)
  const getMatchDateTime = (m: Match): number => {
    const dt = new Date(`${m.date} ${m.time}`);
    const t = dt.getTime();
    return isNaN(t) ? Date.now() : t;
  };

  const getTimeDifference = (m: Match): number => {
    const now = Date.now();
    return Math.abs(now - getMatchDateTime(m));
  };

  // Unified sort metric based on selected sort mode
  const getSortMetric = (m: Match): number => {
    const timestamp = getMatchDateTime(m);
    switch (sortMode) {
      case 'date_desc':
        // Negative timestamp so newer dates have smaller metric and come first
        return -timestamp;
      case 'date_asc':
        return timestamp;
      case 'relevant':
      default:
        return getTimeDifference(m);
    }
  };

  const handleMatchClick = (matchId: string) => {
    // Extract organization ID from current URL to maintain organization context
    const orgIdFromPath = extractOrgIdFromPath(location.pathname);
    if (orgIdFromPath) {
      navigate(`/o/${orgIdFromPath}/score/${matchId}-match-details`);
    } else {
      // Fallback to organization selection if no org context
      navigate('/select-organization');
    }
  };

  const handleEditMatch = (e: React.MouseEvent, match: any) => {
    e.stopPropagation();
    // Find the original database match by ID
    const originalMatch = dbMatches?.find(dbMatch => dbMatch.id === match.id);
    setSelectedMatchForEdit(originalMatch);
    setIsEditModalOpen(true);
  };

  const handleDeleteMatch = (e: React.MouseEvent, match: any) => {
    e.stopPropagation();
    setSelectedMatchForDelete(match);
    setIsDeleteAlertOpen(true);
  };

  const handleLinkVideo = (e: React.MouseEvent, match: any) => {
    e.stopPropagation();
    setSelectedMatchForVideoLink(match);
    setIsVideoLinkModalOpen(true);
  };

  const handleMatchPoints = (e: React.MouseEvent, match: any) => {
    e.stopPropagation();
    // Find the original database match by ID
    const originalMatch = dbMatches?.find(dbMatch => dbMatch.id === match.id);
    console.log('Opening Match Points modal for original match:', originalMatch);
    console.log('Original match penalty scores:', { 
      homePenaltyScore: originalMatch?.home_penalty_score, 
      awayPenaltyScore: originalMatch?.away_penalty_score 
    });
    setSelectedMatchForPoints(originalMatch);
    setIsMatchPointsModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedMatchForDelete) return;
    
    try {
      const { error } = await supabase
        .from('matches')
        .update({ active: false })
        .eq('id', selectedMatchForDelete.id);

      if (error) {
        console.error('Error deactivating match:', error);
        setToast({
          isVisible: true,
          type: 'error',
          message: 'Failed to delete match. Please try again.'
        });
        return;
      }

      // Clear cache to ensure fresh data is loaded
      accessControlService.clearCache();
      
      // Refresh the matches list to reflect the deletion
      await loadMatches();
      
      // Show success toast
      setToast({
        isVisible: true,
        type: 'success',
        message: 'Match deleted successfully!'
      });
      
    } catch (err) {
      console.error('Error deactivating match:', err);
      setToast({
        isVisible: true,
        type: 'error',
        message: 'Failed to delete match. Please try again.'
      });
    } finally {
      setIsDeleteAlertOpen(false);
      setSelectedMatchForDelete(null);
    }
  };

  const openCalendar = () => {
    setIsCalendarOpen(!isCalendarOpen);
  };

  const handleDateRangeSelect = (startDate: Date, endDate: Date) => {
    setSelectedDate(startDate);
    setSelectedStartDate(startDate);
    setSelectedEndDate(endDate);
    setIsCalendarOpen(false);
  };

  const clearDateFilter = () => {
    setSelectedDate(null);
    setSelectedStartDate(null);
    setSelectedEndDate(null);
  };

  // Search function to filter matches based on all text fields
  const searchMatches = (matches: Match[], query: string): Match[] => {
    if (!query.trim()) return matches;
    
    const searchTerm = query.toLowerCase().trim();
    
    return matches.filter(match => {
      // Search in match name
      if (match.name?.toLowerCase().includes(searchTerm)) return true;
      
      // Search in tournament name
      if (match.tournament.name?.toLowerCase().includes(searchTerm)) return true;
      
      // Search in team names
      if (match.homeTeam.name?.toLowerCase().includes(searchTerm)) return true;
      if (match.awayTeam.name?.toLowerCase().includes(searchTerm)) return true;
      
      // Search in venue
      if (match.venue?.toLowerCase().includes(searchTerm)) return true;
      
      // Search in date (formatted)
      const formattedDate = new Date(match.date).toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
      if (formattedDate.toLowerCase().includes(searchTerm)) return true;
      
      // Search in time
      if (match.time?.toLowerCase().includes(searchTerm)) return true;
      
      // Search in status
      if (match.status?.toLowerCase().includes(searchTerm)) return true;
      
      return false;
    });
  };

  // Check if a match is within the selected date range
  const isMatchInSelectedRange = (match: Match) => {
    const matchDate = new Date(match.date);
    
    // If we have a date range
    if (selectedStartDate && selectedEndDate) {
      return matchDate >= selectedStartDate && matchDate <= selectedEndDate;
    }
    
    // If we only have a single date
    if (selectedDate) {
      return matchDate.toDateString() === selectedDate.toDateString();
    }
    
    return true; // Show all matches if no date is selected
  };

  // Note: keep hooks above any early returns

  // Filter matches based on search, favourites and selected date range
  const preStatusFiltered = searchMatches(matches, searchQuery).filter(match => {
    // Filter by favorites tab
    if (activeTopTab === 'favourites') {
      const isFavoriteTournament = favoriteTournaments.includes(match.tournament.id);
      if (!isFavoriteTournament) return false;
    }
    
    // Filter by selected date range
    if (!isMatchInSelectedRange(match)) return false;
    return true;
  });
  
  // Apply status filter with URL sync and counts
  const { status, counts } = useStatusFilter(preStatusFiltered);

  // Normalize status from match object
  const normalizeStatus = (s?: string): BasicStatus | undefined => {
    if (!s) return undefined;
    if (s === 'finished' || s === 'completed') return 'completed';
    if (s === 'upcoming' || s === 'live') return s;
    return undefined;
  };

  // Determine which statuses are active:
  // - if URL status is not 'all', we respect that single status
  // - otherwise we use the multi-select selection
  const activeStatuses: BasicStatus[] =
    status === 'all'
      ? statusSelection
      : (status === 'upcoming' || status === 'live' || status === 'completed'
          ? [status]
          : statusSelection);

  // Final filtered matches based on multi-select
  const filteredMatches = preStatusFiltered.filter((match) => {
    const s = normalizeStatus(match.status);
    if (!s) return false;
    if (activeStatuses.length === 3) return true;
    return activeStatuses.includes(s);
  });
  
  // Force re-computation when favorites or status selection change
  const key = `${activeTopTab}-${status}-${favoritesVersion}-${statusSelection.join(',')}`;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <TabLoader message="Loading matches..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Error Loading Matches</h2>
          <p className="text-gray-600 mb-4">{error}</p>
        </div>
      </div>
    );
  }

  // Group matches by tournament and sort within each group
  const groupMatchesByTournament = (matchList: Match[]) => {
    const grouped: { [key: string]: Match[] } = {};
    
    matchList.forEach(match => {
      const tournamentId = match.tournament.id;
      if (!grouped[tournamentId]) {
        grouped[tournamentId] = [];
      }
      grouped[tournamentId].push(match);
    });
    
    // Sort matches within each tournament by current sort metric
    Object.keys(grouped).forEach(tournamentId => {
      grouped[tournamentId].sort((a, b) => {
        return getSortMetric(a) - getSortMetric(b);
      });
    });
    
    return grouped;
  };

  // Sort tournaments by the best (minimum) sort metric among their matches
  const sortTournamentsByTime = (groupedMatches: { [key: string]: Match[] }) => {
    return Object.entries(groupedMatches).sort(([, matchesA], [, matchesB]) => {
      const metricA = Math.min(...matchesA.map(getSortMetric));
      const metricB = Math.min(...matchesB.map(getSortMetric));
      return metricA - metricB;
    });
  };

  // Sort flat list using the selected sort mode, then group
  const sortedMatches = [...filteredMatches].sort((a, b) => getSortMetric(a) - getSortMetric(b));
  const groupedMatches = groupMatchesByTournament(sortedMatches);
  const sortedTournaments = sortTournamentsByTime(groupedMatches);

  return (
    <div key={key} className="min-h-screen bg-gray-50">
      {/* Consistent Header - Sticky */}
      <ScoreAppHeader
        sticky={true}
        isMobile={actualIsMobile}
        onToggleSidebar={onToggleSidebar}
        leftContent={
          <div className={`flex ${actualIsMobile ? 'space-x-4' : 'space-x-8'}`}>
            <button
              onClick={() => setActiveTopTab('all')}
              className={`pb-2 px-1 border-b-2 font-medium ${actualIsMobile ? 'text-xs' : 'text-sm'} min-h-44 ${
                activeTopTab === 'all'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setActiveTopTab('favourites')}
              className={`pb-2 px-1 border-b-2 font-medium ${actualIsMobile ? 'text-xs' : 'text-sm'} min-h-44 ${
                activeTopTab === 'favourites'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Favourites
            </button>
          </div>
        }
        centerContent={
          <div className="flex-1 flex justify-center">
            <div className="relative w-full max-w-md">
              <div className={`absolute inset-y-0 left-0 ${actualIsMobile ? 'pl-3' : 'pl-3'} flex items-center pointer-events-none`}>
                <svg className={`${actualIsMobile ? 'h-4 w-4' : 'h-5 w-5'} text-gray-400`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                type="text"
                placeholder={actualIsMobile ? "Search..." : "Search matches, teams, tournaments..."}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`block w-full ${actualIsMobile ? 'pl-8 pr-3 py-2' : 'pl-10 pr-3 py-2'} border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${actualIsMobile ? 'text-sm' : 'sm:text-sm'}`}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className={`absolute inset-y-0 right-0 pr-3 flex items-center min-h-44 min-w-44`}
                >
                  <svg className={`${actualIsMobile ? 'h-4 w-4' : 'h-5 w-5'} text-gray-400 hover:text-gray-600`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
              )}
            </div>
          </div>
        }
        rightContent={
          <div className={`flex items-center ${actualIsMobile ? 'space-x-2' : 'space-x-3'}`}>
            <ViewModeToggle value={viewMode} onChange={setViewMode} aria-label="Match view" />
            <SortModeToggle value={sortMode} onChange={setSortMode} aria-label="Sort matches" />
            {/* Status Filter - multi-select dropdown */}
            <StatusFilterDropdown
              selected={statusSelection}
              onChange={setStatusSelection}
              counts={counts}
            />
            {/* Date Range Selector */}
            <div className="relative">
              <button 
                ref={dateButtonRef}
                onClick={openCalendar}
                className={`${actualIsMobile ? 'p-2' : 'p-2'} bg-gray-100 rounded-lg hover:bg-gray-200 cursor-pointer min-h-44 min-w-44 flex items-center justify-center`}
                title={selectedStartDate && selectedEndDate 
                  ? `${selectedStartDate.getDate().toString().padStart(2, '0')}/${(selectedStartDate.getMonth() + 1).toString().padStart(2, '0')} - ${selectedEndDate.getDate().toString().padStart(2, '0')}/${(selectedEndDate.getMonth() + 1).toString().padStart(2, '0')}`
                  : 'Select Date Range'
                }
              >
              <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              </button>
              
              {/* Dual Calendar Dropdown */}
              {isCalendarOpen && (
                <DualCalendarDropdown
                  isOpen={isCalendarOpen}
                  onClose={() => setIsCalendarOpen(false)}
                  onDateRangeSelect={handleDateRangeSelect}
                  onClearFilter={clearDateFilter}
                  selectedStartDate={selectedStartDate || undefined}
                  selectedEndDate={selectedEndDate || undefined}
                  triggerRef={dateButtonRef}
                />
              )}
            </div>

            {/* Create Match Button */}
            <Button
              onClick={() => setIsCreateModalOpen(true)}
              variant="primary"
              size={actualIsMobile ? "sm" : "md"}
              className="min-h-44"
            >
              {actualIsMobile ? '+ Match' : '+ Match'}
            </Button>
          </div>
        }
      />

      {/* Status chips are in the header; extra tabs removed */}

      {/* Matches List */}
      <div className={`${actualIsMobile ? 'p-3' : 'p-4'}`}>
        {sortedTournaments.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-400 text-6xl mb-4">
              {searchQuery ? '🔍' : '⚽'}
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchQuery ? 'No matches found' : 'No matches found'}
            </h3>
            <p className="text-gray-500">
              {searchQuery 
                ? `No matches found for "${searchQuery}". Try a different search term.`
                : 'Try adjusting your filters or check back later.'
              }
            </p>
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="mt-4 text-blue-600 hover:text-blue-700 text-sm font-medium"
              >
                Clear search
              </button>
            )}
          </div>
        ) : viewMode === 'cards' ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {sortedMatches.map((match) => (
              <MatchCard
                key={match.id}
                match={match}
                onMatchClick={handleMatchClick}
                onEditMatch={(e, m) => {}}
                onDeleteMatch={(e, m) => {}}
                onLinkVideo={(e, m) => {}}
              />
            ))}
          </div>
        ) : (
          <div className={`${actualIsMobile ? 'space-y-4' : 'space-y-6'}`}>
            {sortedTournaments.map(([tournamentId, tournamentMatches]) => {
              const firstMatch = tournamentMatches[0];
              return (
                <div key={tournamentId} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                  {/* Tournament Header - Only show on desktop */}
                  {!actualIsMobile && (
                  <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                  <div className="flex items-center space-x-2">
                      <img 
                        src={firstMatch.tournament.logo} 
                        alt={`${firstMatch.tournament.name} logo`}
                        className="w-5 h-5 rounded object-cover"
                        onError={(e) => {
                          e.currentTarget.src = 'https://via.placeholder.com/20x20/cccccc/666666?text=⚽';
                        }}
                      />
                      <span className="text-sm font-semibold text-gray-900">{firstMatch.tournament.name}</span>
                  </div>
                </div>
                  )}

                  {/* Tournament Matches */}
                  <div className="divide-y divide-gray-100">
                    {tournamentMatches.map((match) => (
                      <div key={match.id} className={`${actualIsMobile ? 'px-3 py-3' : 'px-4 py-2'} hover:bg-gray-50 transition-colors ${actualIsMobile ? '' : 'cursor-pointer'} touch-manipulation`} onClick={actualIsMobile ? undefined : () => handleMatchClick(match.id)}>
                        {actualIsMobile ? (
                          /* Mobile Layout */
                          <div className="space-y-2">
                            {/* Tournament Name */}
                            <div className="text-xs font-semibold text-gray-600 truncate">
                              {firstMatch.tournament.name}
                            </div>
                            {/* Match name */}
                            <div className="text-sm font-medium text-gray-900 truncate" title={match.name || `${match.homeTeam.name} vs ${match.awayTeam.name}`}>
                              {match.name || `${match.homeTeam.name} vs ${match.awayTeam.name}`}
                            </div>
                            
                            {/* Date and Time Row */}
                            <div className="flex justify-between items-center">
                              <span className="text-xs text-gray-500">{match.date}</span>
                              <span className="text-xs font-medium text-gray-900">{match.time}</span>
                            </div>
                            
                            {/* Teams and Scores */}
                            <div className="space-y-2">
                              {/* Home Team */}
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-2">
                                  <img 
                                    src={match.homeTeam.logo} 
                                    alt={`${match.homeTeam.name} logo`}
                                    className="w-5 h-5 rounded object-cover"
                                    onError={(e) => {
                                      e.currentTarget.src = 'https://via.placeholder.com/20x20/cccccc/666666?text=🏟️';
                                    }}
                                  />
                                  <span className="text-sm font-medium text-gray-900 truncate">{match.homeTeam.name}</span>
                                </div>
                                <span className="text-sm font-bold text-gray-900">{match.homeTeam.score}</span>
                              </div>
                              
                              {/* Away Team */}
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-2">
                                  <img 
                                    src={match.awayTeam.logo} 
                                    alt={`${match.awayTeam.name} logo`}
                                    className="w-5 h-5 rounded object-cover"
                                    onError={(e) => {
                                      e.currentTarget.src = 'https://via.placeholder.com/20x20/cccccc/666666?text=🏟️';
                                    }}
                                  />
                                  <span className="text-sm font-medium text-gray-900 truncate">{match.awayTeam.name}</span>
                                </div>
                                <span className="text-sm font-bold text-gray-900">{match.awayTeam.score}</span>
                              </div>
                            </div>
                            
                          </div>
                        ) : (
                          /* Desktop Layout - 2-line format for smaller screens */
                          <div className="space-y-1.5">
                            {/* Line 1: Match name + date/time */}
                            <div className="flex items-center justify-between gap-2 min-w-0">
                              <span 
                                className="text-sm font-medium text-gray-900 truncate flex-1 min-w-0" 
                                title={match.name || `${match.homeTeam.name} vs ${match.awayTeam.name}`}
                              >
                                {match.name || `${match.homeTeam.name} vs ${match.awayTeam.name}`}
                              </span>
                              <span className="text-xs text-gray-500 flex-shrink-0">{match.date} · {match.time}</span>
                            </div>
                            {/* Line 2: Teams, scores, status, actions */}
                            <div className="flex items-center justify-between gap-2 flex-wrap">
                              <div className="flex items-center justify-start space-x-4 min-w-0 flex-1">
                                <div className="flex items-center space-x-2 min-w-0">
                                  <img 
                                    src={match.homeTeam.logo} 
                                    alt={`${match.homeTeam.name} logo`}
                                    className="w-5 h-5 rounded object-cover flex-shrink-0"
                                    onError={(e) => {
                                      e.currentTarget.src = 'https://via.placeholder.com/20x20/cccccc/666666?text=🏟️';
                                    }}
                                  />
                                  <span className="text-sm font-medium text-gray-900 truncate">{match.homeTeam.name}</span>
                                </div>
                                {match.status === 'upcoming' ? (
                                  <span className="text-gray-400 text-sm font-medium">vs</span>
                                ) : (
                                  <div className="flex flex-col items-center flex-shrink-0">
                                    <span className="text-sm font-bold text-gray-900">{match.homeTeam.score} - {match.awayTeam.score}</span>
                                    {match.homePenaltyScore !== undefined && match.awayPenaltyScore !== undefined && 
                                     (match.homePenaltyScore > 0 || match.awayPenaltyScore > 0) && (
                                      <span className="text-xs text-gray-500">
                                        penalties ({match.homePenaltyScore}) - ({match.awayPenaltyScore})
                                      </span>
                                    )}
                                  </div>
                                )}
                                <div className="flex items-center space-x-2 min-w-0">
                                  <span className="text-sm font-medium text-gray-900 truncate">{match.awayTeam.name}</span>
                                  <img 
                                    src={match.awayTeam.logo} 
                                    alt={`${match.awayTeam.name} logo`}
                                    className="w-5 h-5 rounded object-cover flex-shrink-0"
                                    onError={(e) => {
                                      e.currentTarget.src = 'https://via.placeholder.com/20x20/cccccc/666666?text=🏟️';
                                    }}
                                  />
                                </div>
                              </div>
                              <div className="flex items-center space-x-2 flex-shrink-0">
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                  match.status === 'live'
                                    ? 'bg-red-100 text-red-700'
                                    : match.status === 'upcoming'
                                    ? 'bg-blue-100 text-blue-700'
                                    : 'bg-gray-100 text-gray-700'
                                }`}>
                                  {match.status === 'live' ? 'LIVE' : match.status === 'upcoming' ? 'UPCOMING' : 'COMPLETED'}
                                </span>
                                {/* Actions */}
                                <div className="flex items-center space-x-1">
                            {/* Edit Button */}
                            <button
                              onClick={(e) => handleEditMatch(e, match)}
                                className="p-1 text-gray-400 hover:text-blue-600 transition-colors min-h-44 min-w-44"
                              title="Edit match"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                            
                            {/* Delete Button */}
                            <button
                              onClick={(e) => handleDeleteMatch(e, match)}
                              className="p-1 text-gray-400 hover:text-red-600 transition-colors min-h-44 min-w-44"
                              title="Delete match"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>

                            {/* Match Points Button - Only show for completed matches */}
                            {status === 'completed' && (
                              <button
                                onClick={(e) => handleMatchPoints(e, match)}
                                className={`p-1 transition-colors min-h-44 min-w-44 ${
                                  hasMatchPoints(match.id) 
                                    ? 'text-green-600 hover:text-green-700' 
                                    : 'text-gray-400 hover:text-green-600'
                                }`}
                                title={hasMatchPoints(match.id) ? "Match points already set" : "Set match points"}
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                </svg>
                              </button>
                            )}

                            {/* Link Button */}
                            <button
                              onClick={(e) => handleLinkVideo(e, match)}
                              className="p-1 text-gray-400 hover:text-blue-600 transition-colors min-h-44 min-w-44"
                              title="View video links"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                              </svg>
                            </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
              </div>
            ))}
                  </div>
                </div>
              );
            })}
          </div>
        ) }
      </div>

      {/* Create Match Modal */}
      {isCreateModalOpen && (
        <CreateMatchModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onSuccess={async () => {
            setIsCreateModalOpen(false);
            // Show success toast immediately
            setToast({
              isVisible: true,
              type: 'success',
              message: 'Match created successfully!'
            });
            // Clear cache and refresh the matches list to show the new match
            accessControlService.clearCache();
            await loadMatches();
          }}
        />
      )}

      {/* Edit Match Modal */}
      {isEditModalOpen && (
        <CreateMatchModal
          isOpen={isEditModalOpen}
          mode="edit"
          matchData={selectedMatchForEdit}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedMatchForEdit(null);
          }}
          onSuccess={async () => {
            setIsEditModalOpen(false);
            setSelectedMatchForEdit(null);
            // Show success toast immediately
            setToast({
              isVisible: true,
              type: 'success',
              message: 'Match details successfully updated!'
            });
            // Clear cache and refresh the matches list to show updated data
            accessControlService.clearCache();
            await loadMatches();
          }}
        />
      )}

      {/* Delete Alert */}
      <DeleteAlert
        isOpen={isDeleteAlertOpen}
        onClose={() => {
          setIsDeleteAlertOpen(false);
          setSelectedMatchForDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Delete"
        description="Are you sure you want to delete this match? It will be hidden from the match listing."
      />

      {/* Toast Notification */}
      <Toast
        isVisible={toast.isVisible}
        onClose={() => setToast(prev => ({ ...prev, isVisible: false }))}
        type={toast.type}
        message={toast.message}
        duration={6000}
      />

      {/* Video Link Modal */}
      <VideoLinkModal
        isOpen={isVideoLinkModalOpen}
        onClose={() => {
          setIsVideoLinkModalOpen(false);
          setSelectedMatchForVideoLink(null);
        }}
        onSuccess={async (videoUrls: string[]) => {
          console.log('Videos linked successfully:', videoUrls);
          setToast({
            isVisible: true,
            type: 'success',
            message: 'Video links updated successfully!'
          });
          setIsVideoLinkModalOpen(false);
          setSelectedMatchForVideoLink(null);
        }}
        matchName={selectedMatchForVideoLink?.name}
        matchId={selectedMatchForVideoLink?.id}
      />

      {/* Match Points Modal */}
      {isMatchPointsModalOpen && selectedMatchForPoints && (
        <MatchPointsModal
          isOpen={isMatchPointsModalOpen}
          onClose={() => {
            setIsMatchPointsModalOpen(false);
            setSelectedMatchForPoints(null);
          }}
          onSuccess={async () => {
            setIsMatchPointsModalOpen(false);
            setSelectedMatchForPoints(null);
            // Show success toast immediately
            setToast({
              isVisible: true,
              type: 'success',
              message: 'Match points saved successfully!'
            });
            // Clear cache and refresh the matches list
            accessControlService.clearCache();
            await loadMatches();
          }}
          match={{
            id: selectedMatchForPoints.id,
            name: selectedMatchForPoints.name,
            home_team: {
              id: selectedMatchForPoints.home_team_id,
              name: selectedMatchForPoints.home_team?.name || 'Home Team',
              logo: selectedMatchForPoints.home_team?.logo
            },
            away_team: {
              id: selectedMatchForPoints.away_team_id,
              name: selectedMatchForPoints.away_team?.name || 'Away Team',
              logo: selectedMatchForPoints.away_team?.logo
            },
            home_score: selectedMatchForPoints.home_score || 0,
            away_score: selectedMatchForPoints.away_score || 0,
            homePenaltyScore: selectedMatchForPoints.home_penalty_score,
            awayPenaltyScore: selectedMatchForPoints.away_penalty_score,
            tournament_id: selectedMatchForPoints.tournament_id,
            season_id: selectedMatchForPoints.season_id
          }}
        />
      )}
    </div>
  );
};

export default MatchSelectionScreen;
